import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { ISenderIdentity } from '../../../../interfaces/models/campaign';
import InfoTip from './InfoTip';

/**
 * Which people this campaign sends as.
 *
 * Senders used to be derived from the campaign's LinkedIn accounts with nothing to
 * choose — so a workspace with two accounts could not run a campaign as just one of
 * them. Picking here pins the choice on the campaign; until someone does, the derived
 * behaviour is unchanged.
 *
 * Every row reports the two halves separately, because they fail independently and
 * need different fixes: a sender with no mailbox still runs LinkedIn steps perfectly
 * well, and saying "not ready" would send people looking for the wrong problem.
 */

interface Props {
  /** Every sender in the workspace — the full set to choose from. */
  options: ISenderIdentity[];
  /** Currently selected ids. */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /**
   * Called as the panel opens — the moment the rows are read, so the moment they
   * should be current. Settings in another tab can have changed them since.
   */
  onOpen?: () => void;
  /** Starts the mailbox connect flow for one sender. */
  onConnectMailbox: (identity: ISenderIdentity) => void;
  disabled?: boolean;
  /**
   * Paint the field as invalid — set once the wizard has refused to continue.
   * What is wrong is stated in the message beneath; this is what makes the eye find
   * the field the message is about.
   */
  invalid?: boolean;
}

function initialsOf(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function displayName(i: ISenderIdentity): string {
  return i.name || i.linkedin?.name || i.linkedin?.email || 'Unnamed sender';
}

/** Why the LinkedIn half is not usable, or null when it is. */
function linkedInProblem(i: ISenderIdentity): string | null {
  if (i.linkedin?.kind === 'extension') return null;
  if (!i.linkedin?.accountId) return 'No LinkedIn account connected.';
  if (i.linkedin?.status === 'not_found') return 'This account no longer exists at the provider. Reconnect it.';
  if (i.linkedin?.status === 'credentials') return 'LinkedIn is asking for the credentials again.';
  return null;
}

/** Why the mailbox is not usable, or null when it is. */
function mailboxProblem(i: ISenderIdentity): string | null {
  if (!i.mail?.connected) return 'No mailbox connected — email steps will skip this sender.';
  if (i.mail?.pausedAt) return 'Sending is paused for this mailbox.';
  if (i.mail?.status && i.mail.status !== 'connected') return 'This mailbox needs reconnecting.';
  return null;
}

const CampaignSenderPicker: React.FC<Props> = ({
  options, selectedIds, onChange, onOpen, onConnectMailbox, disabled, invalid = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const selected = useMemo(
    () => options.filter(i => selectedIds.includes(i._id)),
    [options, selectedIds],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(i =>
      displayName(i).toLowerCase().includes(q)
      || (i.mail?.address ?? '').toLowerCase().includes(q));
  }, [options, query]);

  const toggle = useCallback((id: string) => {
    onChange(selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id]);
  }, [selectedIds, onChange]);

  const allSelected = options.length > 0 && selectedIds.length === options.length;

  /*
    The field's own summary. One sender reads as a name; several read as a count, with
    their initials in front — the names themselves would not fit and truncating them
    loses exactly the part that tells them apart.
  */
  const summary = selected.length === 0
    ? 'No sender selected'
    : selected.length === 1
      ? displayName(selected[0])
      : `${selected.length} accounts selected`;

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        $open={open}
        $invalid={invalid}
        disabled={disabled}
        onClick={() => {
          if (!open) onOpen?.();
          setOpen(!open);
        }}
        aria-expanded={open}
      >
        <Avatars>
          {selected.slice(0, 3).map(i => (
            <Avatar key={i._id}>{initialsOf(displayName(i))}</Avatar>
          ))}
        </Avatars>
        <Summary $muted={selected.length === 0}>{summary}</Summary>
        <Chevron $open={open} aria-hidden>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Chevron>
      </Trigger>

      {open && (
        <Panel>
          <Search
            autoFocus
            placeholder="Search by name"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />

          <GroupRow>
            <Check
              type="checkbox"
              checked={allSelected}
              // A partial selection is neither on nor off; the dash says so.
              ref={el => { if (el) el.indeterminate = !allSelected && selectedIds.length > 0; }}
              onChange={() => onChange(allSelected ? [] : options.map(i => i._id))}
            />
            <GroupLabel>Accounts</GroupLabel>
          </GroupRow>

          <List>
            {filtered.length === 0 && <Empty>No accounts match that.</Empty>}

            {filtered.map(i => {
              const liProblem = linkedInProblem(i);
              const mbProblem = mailboxProblem(i);
              const incomplete = !!liProblem || !!mbProblem;

              return (
                <Rowk key={i._id}>
                  <Check
                    type="checkbox"
                    checked={selectedIds.includes(i._id)}
                    onChange={() => toggle(i._id)}
                  />
                  <Avatar>{initialsOf(displayName(i))}</Avatar>

                  <Who>
                    <Name>{displayName(i)}</Name>
                    {incomplete && <Sub>Setup incomplete</Sub>}
                  </Who>

                  {/* Channel icon, label, then a status badge — the icon is what the
                      eye finds first when scanning a list of accounts, and it says
                      which channel without reading. */}
                  <Halves>
                    <Half>
                      <LinkedInMark aria-hidden>in</LinkedInMark>
                      LinkedIn
                      {liProblem
                        ? <InfoTip text={liProblem}><Bad aria-label={liProblem}><BangIcon /></Bad></InfoTip>
                        : <InfoTip text="Connected"><Good aria-label="Connected"><CheckIcon /></Good></InfoTip>}
                    </Half>
                    <Half>
                      <EnvelopeIcon />
                      Mailbox
                      {mbProblem
                        ? <InfoTip text={mbProblem}><Bad aria-label={mbProblem}><BangIcon /></Bad></InfoTip>
                        : <InfoTip text="Connected"><Good aria-label="Connected"><CheckIcon /></Good></InfoTip>}
                    </Half>
                  </Halves>

                  {/* The fix, next to the problem. A mailbox is the one thing that can
                      be started from here — a broken LinkedIn account is repaired in
                      settings, where its credentials live. */}
                  {!i.mail?.connected && (
                    <Fix type="button" onClick={() => onConnectMailbox(i)}>Connect mailbox</Fix>
                  )}
                </Rowk>
              );
            })}
          </List>
        </Panel>
      )}
    </Wrap>
  );
};

export default CampaignSenderPicker;

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Wrap = styled.div`position: relative; width: 100%;`;

const Trigger = styled.button<{ $open: boolean; $invalid?: boolean }>`
  display: flex; align-items: center; gap: 8px; width: 100%; box-sizing: border-box;
  /* Invalid outranks open: a field the wizard just refused should stay marked while
     the user opens it to fix it. */
  border: 1.5px solid ${p => (p.$invalid ? '#fca5a5' : p.$open ? ACCENT : '#e2e8f0')};
  border-radius: 10px; background: #fff; cursor: pointer;
  padding: 8px 12px; min-height: 40px;
  &:hover:not(:disabled) { border-color: ${ACCENT}; }
  &:disabled { opacity: .6; cursor: default; }
`;

const Avatars = styled.div`
  display: flex; align-items: center; flex-shrink: 0;
  /* Overlapped, so several initials read as a group rather than a list. */
  & > * + * { margin-left: -6px; }
`;

const Avatar = styled.span`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 22px; height: 22px; border-radius: 50%;
  background: ${styles.colors.primary100}; color: ${ACCENT};
  border: 1.5px solid #fff;
  font-family: NunitoSans; font-size: 9px; font-weight: 800;
`;

const Summary = styled.span<{ $muted?: boolean }>`
  flex: 1; min-width: 0; text-align: left;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: NunitoSans; font-size: 13px; font-weight: 600;
  color: ${p => (p.$muted ? '#94a3b8' : '#0f172a')};
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: flex; align-items: center; flex-shrink: 0; color: #64748b;
  transform: ${p => (p.$open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.15s;
  svg { display: block; }
`;

const Panel = styled.div`
  position: absolute; z-index: 60; left: 0; right: 0; top: calc(100% + 6px);
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  box-shadow: 0 10px 30px rgba(20,30,80,0.12);
  padding: 10px;
  display: flex; flex-direction: column; gap: 8px;
`;

const Search = styled.input`
  width: 100%; box-sizing: border-box; height: 34px;
  border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 0 10px;
  font-family: NunitoSans; font-size: 13px; color: #0f172a;
  &:focus { outline: none; border-color: ${ACCENT}; }
  &::placeholder { color: #b6c0cd; }
`;

const GroupRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 2px 2px 0;
`;

const GroupLabel = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700; color: #94a3b8;
`;

const Check = styled.input`
  width: 15px; height: 15px; flex-shrink: 0; cursor: pointer;
  accent-color: ${ACCENT};
`;

const List = styled.div`
  display: flex; flex-direction: column;
  max-height: 260px; overflow-y: auto;
  /* The row is what should give, not the panel. Without this a long name pushed
     the status badges wider than the dropdown and produced a horizontal bar. */
  overflow-x: hidden;
`;

/*
 * Wraps, so "Connect mailbox" can never be pushed out of sight.
 *
 * The list clips horizontally — it has to, or a long name drags a scrollbar across the
 * dropdown. But the status badges and the fix link both refuse to shrink, so on a
 * narrow panel the link was simply cut off the right edge: the one row that needed an
 * action was the one row that did not show one. Allowed to wrap, it drops onto a
 * second line instead of disappearing.
 */
const Rowk = styled.label`
  display: flex; align-items: center; gap: 10px; min-width: 0; flex-wrap: wrap;
  padding: 8px 4px; border-radius: 8px; cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const Who = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column;
`;

const Name = styled.span`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #0f172a;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const Sub = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

/*
 * The two channels need real air between them.
 *
 * At 12px the badge belonging to LinkedIn sat as close to the envelope on its right as
 * to the word on its left, so with a "!" on one of them it was genuinely ambiguous
 * which channel was complaining. The gap is now wider than the 5px inside each half, so
 * the grouping reads before the labels do.
 */
const Halves = styled.div`
  display: flex; align-items: center; gap: 24px; flex-shrink: 0;
`;

const Half = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-family: NunitoSans; font-size: 11px; color: #64748b;
  white-space: nowrap;
`;

/* LinkedIn's own mark, in its own blue — a generic glyph here reads as decoration
   rather than as "this is the LinkedIn half of the pair". */
const LinkedInMark = styled.span`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 16px; height: 16px; border-radius: 3px;
  background: #0a66c2; color: #fff;
  font-family: NunitoSans; font-size: 9px; font-weight: 800; line-height: 1;
`;

const CheckIcon = () => (
  <svg width="9" height="7" viewBox="0 0 10 8" fill="none" style={{ display: 'block' }}>
    <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BangIcon = () => (
  <svg width="3" height="9" viewBox="0 0 3 9" fill="currentColor" style={{ display: 'block' }}>
    <rect x="0" y="0" width="3" height="6" rx="1.5" />
    <circle cx="1.5" cy="8" r="1.5" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0 }}>
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

/* Filled discs, not bare glyphs: a green tick and a red bang have to be separable at
   a glance and for anyone who cannot tell the two colours apart. */
const Good = styled.span`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 16px; height: 16px; border-radius: 50%;
  background: #22c55e; color: #fff;
  cursor: help;
`;

const Bad = styled.span`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 16px; height: 16px; border-radius: 50%;
  background: #ef4444; color: #fff;
  cursor: help;
`;

const Fix = styled.button`
  flex-shrink: 0;
  /* Right-aligned when the row fits, and the first thing to take the second line
     when it does not — see Rowk. */
  margin-left: auto;
  border: none; background: none; cursor: pointer; padding: 2px 4px;
  font-family: NunitoSans; font-size: 11px; font-weight: 700; color: ${ACCENT};
  &:hover { text-decoration: underline; }
`;

const Empty = styled.div`
  padding: 14px 4px; text-align: center;
  font-family: NunitoSans; font-size: 12px; color: #94a3b8;
`;
